import React, { useState, useEffect, useCallback } from "react";
import { X, Plus, Trash2, Loader2, Lock, Check, Package } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api/admin/materials`;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * MaterialIssueModal — records the decoration materials used for a booking,
 * priced from the central Material Rate Master. On "Finalize", the server
 * freezes a snapshot (name, qty, unit, rate, tax, line total) that later rate
 * changes never touch. vendors = shop vendors list for per-line supplier.
 */
const MaterialIssueModal = ({ booking, vendors = [], authAxios, onClose, onChanged }) => {
  const [available, setAvailable] = useState([]);
  const [lines, setLines] = useState([]);
  const [locked, setLocked] = useState(false);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API}/booking/issue`, { params: { booking_id: booking.id } });
      setAvailable(r.data.available || []);
      if (r.data.issue) {
        setExisting(r.data.issue);
        setLocked(!!r.data.issue.locked);
        setNote(r.data.issue.supplier_note || "");
        setLines((r.data.issue.lines || []).map((l) => ({
          _uid: (crypto?.randomUUID?.() || `${l.material_id}-${Math.random()}`),
          material_id: l.material_id, quantity: l.quantity, vendor_id: l.vendor_id || "", vendor_name: l.vendor_name || "",
        })));
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load materials"); }
    setLoading(false);
  }, [authAxios, booking.id]);
  useEffect(() => { load(); }, [load]);

  const matById = (id) => available.find((m) => m.id === id) || existing?.lines?.find((l) => l.material_id === id);
  const addLine = () => setLines((p) => [...p, { _uid: (crypto?.randomUUID?.() || `new-${Date.now()}-${p.length}`), material_id: available[0]?.id || "", quantity: 1, vendor_id: "", vendor_name: "" }]);
  const updateLine = (i, k, v) => setLines((p) => p.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const removeLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));

  const computed = lines.map((l) => {
    const m = matById(l.material_id);
    const rate = m?.standard_rate ?? m?.rate ?? 0;
    const taxPct = (m?.tax?.percent ?? m?.tax_percent) || 0;
    const lineTotal = rate * (Number(l.quantity) || 0);
    const tax = lineTotal * taxPct / 100;
    return { rate, taxPct, lineTotal, tax, name: m?.name, unit: m?.unit };
  });
  const subtotal = computed.reduce((s, c) => s + c.lineTotal, 0);
  const taxTotal = computed.reduce((s, c) => s + c.tax, 0);
  const grand = subtotal + taxTotal;

  const save = async (finalize) => {
    if (lines.length === 0 || lines.some((l) => !l.material_id)) { toast.error("Add at least one material"); return; }
    if (finalize && !window.confirm("Finalize locks these charges. Later rate changes won't affect this booking. Continue?")) return;
    setSaving(true);
    try {
      const payload = {
        booking_id: booking.id,
        lines: lines.map((l) => {
          const v = vendors.find((x) => x.id === l.vendor_id);
          return { material_id: l.material_id, quantity: Number(l.quantity), vendor_id: l.vendor_id || null, vendor_name: v?.name || l.vendor_name || null };
        }),
        supplier_note: note, finalize,
      };
      const r = await authAxios.post(`${API}/booking/issue`, payload);
      toast.success(finalize ? `Finalized — ${fmtINR(r.data.grand_total)}` : "Draft saved");
      if (onChanged) onChanged();
      if (finalize) onClose(); else load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="material-issue-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2"><Package size={20} className="text-[#DC283C]" /> Material Issue</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#64748B] mb-4">{booking.customer_name} · <span className="font-mono">{booking.id}</span></p>

        {locked && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#475569] flex items-center gap-2 mb-4" data-testid="material-issue-locked">
            <Lock size={14} /> These charges are finalized and locked. Rates shown are the frozen snapshot.
          </div>
        )}

        {loading ? <div className="py-10 text-center"><Loader2 className="animate-spin inline text-[#DC283C]" /></div> : locked && existing ? (
          <>
            {/* Frozen snapshot — rates are exactly what was charged at finalize time */}
            <div className="space-y-2">
              {(existing.lines || []).map((l, i) => (
                <div key={l.material_id || i} className="flex items-center gap-2 border border-gray-100 rounded-xl p-2 text-sm" data-testid={`material-issue-line-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1E1B4B] truncate">{l.name}</p>
                    <p className="text-[11px] text-gray-400">{l.quantity} {l.unit} × {fmtINR(l.rate)}{l.vendor_name ? ` · ${l.vendor_name}` : ""}</p>
                  </div>
                  <span className="w-24 text-right font-semibold text-[#1E1B4B]">{fmtINR(l.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-[#FFF7F8] rounded-xl p-3 text-sm space-y-1">
              <div className="flex justify-between text-[#64748B]"><span>Subtotal</span><span>{fmtINR(existing.subtotal)}</span></div>
              <div className="flex justify-between text-[#64748B]"><span>Tax</span><span>{fmtINR(existing.tax_total)}</span></div>
              <div className="flex justify-between font-bold text-[#1E1B4B] text-base pt-1 border-t border-pink-100"><span>Total material cost</span><span data-testid="material-issue-total">{fmtINR(existing.grand_total)}</span></div>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              {lines.map((l, i) => {
                const c = computed[i];
                return (
                  <div key={l._uid || i} className="flex items-center gap-2 border border-gray-100 rounded-xl p-2" data-testid={`material-issue-line-${i}`}>
                    <select disabled={locked} value={l.material_id} onChange={(e) => updateLine(i, "material_id", e.target.value)}
                      className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-200 rounded-lg disabled:bg-gray-50">
                      {locked && !available.some((m) => m.id === l.material_id) && c?.name && <option value={l.material_id}>{c.name}</option>}
                      {available.map((m) => <option key={m.id} value={m.id}>{m.name} — {fmtINR(m.standard_rate)}/{m.unit}</option>)}
                    </select>
                    <input disabled={locked} type="number" min="0.5" step="0.5" value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)}
                      className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-lg disabled:bg-gray-50" placeholder="Qty" />
                    <select disabled={locked} value={l.vendor_id} onChange={(e) => updateLine(i, "vendor_id", e.target.value)}
                      className="w-32 px-2 py-1.5 text-sm border border-gray-200 rounded-lg disabled:bg-gray-50">
                      <option value="">Supplier…</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                    <span className="w-24 text-right text-sm font-semibold text-[#1E1B4B]">{fmtINR(c?.lineTotal)}</span>
                    {!locked && <button onClick={() => removeLine(i)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>}
                  </div>
                );
              })}
            </div>

            {!locked && (
              <button onClick={addLine} disabled={available.length === 0} className="mt-2 text-sm font-semibold text-[#DC283C] flex items-center gap-1 disabled:opacity-40" data-testid="material-issue-add-line">
                <Plus size={15} /> Add material {available.length === 0 && "(no active rates — add them in Material Rates first)"}
              </button>
            )}

            {!locked && (
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Supplier / issue note (optional)"
                className="w-full mt-3 px-3 py-2 text-sm border border-gray-200 rounded-xl" data-testid="material-issue-note" />
            )}

            {/* Totals */}
            <div className="mt-4 bg-[#FFF7F8] rounded-xl p-3 text-sm space-y-1">
              <div className="flex justify-between text-[#64748B]"><span>Subtotal</span><span>{fmtINR(subtotal)}</span></div>
              <div className="flex justify-between text-[#64748B]"><span>Tax</span><span>{fmtINR(taxTotal)}</span></div>
              <div className="flex justify-between font-bold text-[#1E1B4B] text-base pt-1 border-t border-pink-100"><span>Total material cost</span><span data-testid="material-issue-total">{fmtINR(grand)}</span></div>
            </div>

            {!locked && (
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => save(false)} disabled={saving} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold disabled:opacity-50" data-testid="material-issue-save-draft">Save draft</button>
                <button onClick={() => save(true)} disabled={saving} className="px-5 py-2 rounded-xl bg-[#DC283C] text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2" data-testid="material-issue-finalize">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Finalize & lock
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MaterialIssueModal;
